import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { validate } from '@adornis/validation/decorators.js';
import { minLength } from '@adornis/validation/functions/length.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { BillingPositionOverride } from '../../billingPositions/entities/BillingPositionOverride.js';
import { Group } from '../../groups/entities/Group.js';

/**
 * A booking is a relationship of a person to a group for a specific time. Like a student "books" a school to attend classes.
 * It is technically more a relationship and not a booking, but the client uses this terminology so we mimic it here.
 * Person can have multiple "Bookings" for different groups or even for the same group with different start and end dates and different metadata.
 * A Person can also have the same booking for multiple groups. Like a student books the school for the year 23/24 but books with the same relation and data some microsoft team group.
 * With this we can control the participation of multiple intertwined groups with just one booking.
 * @class
 * @extends MongoEntity
 */
@Entity()
export class Booking extends MongoEntity {
  static override _class = 'Booking';
  static override _collectionName = 'bookings';

  constructor(doc: PartialEntityData<Booking>) {
    super(doc);
  }

  @Field(() => Date, { default: v => v ?? new Date() })
  createdAt: Maybe<Date>;

  // the hash of the entity to easily check for duplicates. Can be unique
  @Field(() => String)
  hash: Maybe<string>;

  @validate(sgNonOptional())
  @Field(() => ID)
  userID: Maybe<string>;

  @validate(minLength(1))
  @Field(() => [ID], { default: v => v ?? [] })
  groupIDs: Maybe<string[]>;

  // @ts-expect-error _id as object id in mongo
  @DataLoadedField(() => [Group], ({ groupIDs }) => ({ _id: { $in: [...(groupIDs ?? [])] } }))
  groups: Maybe<Group[]>;

  @Field(() => Date)
  startDate: Maybe<Date>;

  // the end date is optional. A missing endDate just means the booking of the group is open end
  @Field(() => Date)
  endDate: Maybe<Date>;

  /**
   * What this booking pays when it differs from its group's Abrechnungsposition: its price, never
   * the rhythm. Absent when the booking pays what the group says.
   */
  @Field(() => BillingPositionOverride)
  billingPosition: Maybe<BillingPositionOverride>;

  /**
   * The Stichtag of the last lock export that billed the booking *itself*: a Selbstzahler-Soll, or
   * the one collection of a one-time booking (by mandate or as Selbstzahler). Monthly and yearly
   * mandate collections are recorded on the mandate instead (`SepaMandat.billedOn`), because two
   * guardians may pledge on one booking; `isDueOn` reads the later of the two markers. A billed
   * one-time booking is never offered a template again.
   */
  @Field(() => Date)
  billedOn: Maybe<Date>;

  @Field(() => String)
  bookingReference: Maybe<string>;
}
